import React, {Component} from 'react';
import {connectDynamic} from 'go/components/dynamic/dynamicComponent';
import {connect} from 'react-redux';
import _ from 'lodash';
import ApiActions from 'go/actions/api';
import {API_RULES_GET_SUCCESS, API_EHP_GET_SUCCESS} from 'common/types';
import {TERMS_AND_CONDITIONS_TYPES} from 'app-ui-componentlibrary/dist/common/constants';
import hash from 'object-hash';
import {produce} from 'immer';

const {SPECIAL, CUSTOM, STANDARD} = TERMS_AND_CONDITIONS_TYPES;

const AWARD_RECOMMENDATION = 'awardRecommendation';
const AMENDMENT = 'amendment';
const SUB_AWARD_RECOMMENDATION = 'subAwardRecommendation';

export const removeStaleTerms = (
  termsAndConditionsData,
  availableRules,
  federalShare
) => {
  let terms = _.get(termsAndConditionsData, 'terms', {});
  let savedAvailable = _.get(termsAndConditionsData, 'available', '');
  let alert = _.get(termsAndConditionsData, 'needsAttention', false);

  const flattenedRules = _.flatMap(availableRules, (term) => term);
  const standardAvailable = _.filter(flattenedRules, (term) => term.type === STANDARD);
  const specialAvailable = _.filter(flattenedRules, (term) => term.type === SPECIAL);
  const chosen = _.filter(terms, (term) => term.type !== STANDARD);

  // Seperate all included special and custom terms and check if still valid
  _.forEach(terms, (term) => {
    const isAvailable = _.findIndex(specialAvailable, ['title', term.title]) > -1;
    const toInclude = term.type === CUSTOM || (term.type === SPECIAL && isAvailable);
    const validHoldingAmount =
      !term.financialHold || term.financialHoldAmount <= federalShare;

    // if there is an included term that isn't allowed, trigger alert
    alert = alert || (term.type !== STANDARD && !toInclude) || !validHoldingAmount;
    /* after speaking with BA on 4388, we will not filter out the special terms that are invalid,
     just alert that they are invalid */
  });

  // Check if any available terms changed since last time, alert if so
  alert = alert || !_.isEqual(savedAvailable, hash(specialAvailable));

  let concatted = produce(_.concat(standardAvailable, chosen), (draft) => {
    _.forEach(draft, (term, index) => (term.order = index + 1));
  });

  let termsAndConditionsPayload = {
    terms: concatted,
    available: hash(specialAvailable),
    needsAttention: alert,
  };

  return termsAndConditionsPayload;
};

export class TermsAndConditionsListener extends Component {
  constructor(props) {
    super(props);
    this.state = {
      initializing: true,
      checkedChangesFromLastSessions: false,
      executing: false,
    };
  }

  componentDidMount() {
    this.props.saveEntitySessionData('context.rules', {
      loading: true,
    });
    this.initializeRules();
  }

  componentDidUpdate(prevProps) {
    const currData = _.cloneDeep(_.get(this.props.data, 'payload'));
    const prevData = _.cloneDeep(_.get(prevProps.data, 'payload'));

    const ENTITY = this.props.isAmendment ? AMENDMENT : AWARD_RECOMMENDATION;

    if (ENTITY === AMENDMENT) {
      _.set(prevData, 'grantAmendment.termsAndConditions.needsAttention', undefined);
      _.set(currData, 'grantAmendment.termsAndConditions.needsAttention', undefined);
    } else {
      _.set(prevData, 'termsAndConditions.needsAttention', undefined);
      _.set(currData, 'termsAndConditions.needsAttention', undefined);
    }

    // TODO May need to add or replace that check with check for versionId due to concurrency
    if (!this.state.executing && !_.isEqual(currData, prevData)) {
      this.setState({executing: true}, () => {
        this.initializeRules();
      });
    }
  }

  updateTerms = (rules) => {
    if (!_.get(this.props, 'data.loading', true)) {
      const ENTITY = this.props.isAmendment ? AMENDMENT : AWARD_RECOMMENDATION;

      let newPayload;
      if (ENTITY === AWARD_RECOMMENDATION) {
        newPayload = removeStaleTerms(
          _.get(this.props.data, 'payload.termsAndConditions', {}),
          rules,
          _.get(this.props.data, 'payload.budget.share.fedShare', 0)
        );
      }
      if (ENTITY === AMENDMENT) {
        newPayload = removeStaleTerms(
          _.get(
            this.props.context.amendment,
            'payload.grantAmendment.termsAndConditions',
            {}
          ),
          rules,
          _.get(
            this.props.context.amendment,
            'payload.grantAmendment.budget.share.fedShare',
            0
          )
        );
      }

      if (this.props.loadEHPConditions) {
        this.setState({initializing: true}, () =>
          this.initializeEhpConditions(newPayload)
        );
      } else {
        this.saveTermsAndConditionsData(newPayload);
      }
    }
  };

  saveTermsAndConditionsData = (termsAndConditions) => {
    // If amendment, save to amendment, if preaward, save to award recommendation

    const ENTITY = this.props.isAmendment
      ? AMENDMENT
      : this.props.isSubAward
      ? SUB_AWARD_RECOMMENDATION
      : AWARD_RECOMMENDATION;
    if (ENTITY === AWARD_RECOMMENDATION || ENTITY === SUB_AWARD_RECOMMENDATION) {
      return this.props.saveEntityData(
        `${this.props.pathToBeUsed}.payload.termsAndConditions`,
        ENTITY,
        termsAndConditions
      );
    }

    if (ENTITY === AMENDMENT) {
      return this.props.saveEntityData(
        `${this.props.pathToBeUsed}.payload.grantAmendment.termsAndConditions`,
        ENTITY,
        termsAndConditions
      );
    }
  };

  initializeRules = () => {
    const config = [
      ...(_.get(this.props, 'includeStandard', true)
        ? [
            {
              configType: 'department',
              configId: 'DHS',
              ruleSet: 'termsAndConditions',
            },
            {
              configType: 'agency',
              configId: 'FEMA',
              ruleSet: 'termsAndConditions',
            },
          ]
        : []),
      {
        configType: `${_.get(this.props, 'configType', 'program')}`,
        configId: `${_.get(this.props, 'ruleEntityId', this.props.programId)}`,
        ruleSet: `${_.get(this.props, 'ruleSet', 'termsAndConditions')}`,
      },
    ];
    return this.props
      .getRules(this.props.token, this.props.grantId, config, this.props.subGrantId)
      .then((result) => {
        if (result && result.type === API_RULES_GET_SUCCESS) {
          this.props.saveEntitySessionData('context.rules', {
            loading: false,
            payload: result.payload,
          });
          this.setState(
            {
              initializing: false,
              executing: false,
            },
            () => this.updateTerms(result.payload)
          );
        } else {
          console.log('terms and conditions rules could not load');
        }
      })
      .catch((error) => console.log(error));
  };

  initializeEhpConditions = (termsAndConditions) => {
    let projectIds = [];
    const {programApprovedSubAwards, isSubAward, subGrantId} = this.props;

    if (
      !this.state.executing &&
      programApprovedSubAwards !== undefined &&
      programApprovedSubAwards.loading !== true &&
      programApprovedSubAwards.total > 0
    ) {
      _.forEach(programApprovedSubAwards.items, (item) => {
        projectIds.push(item.subGrantId);
      });
    }
    if (isSubAward) projectIds.push(subGrantId);
    return this.props
      .getEHPConditions(this.props.token, projectIds)
      .then((result) => {
        if (result && result.type === API_EHP_GET_SUCCESS) {
          this.setState(
            {
              initializing: false,
              executing: false,
            },
            () => this.updateEhpConditions(termsAndConditions, result.payload)
          );
        } else {
          console.log('EHP conditions could not be retrieved');
        }
      })
      .catch((error) => console.log(error));
  };

  updateEhpConditions = (termsAndConditions, ehpConditions) => {
    termsAndConditions['ehpConditions'] = ehpConditions;
    this.saveTermsAndConditionsData(termsAndConditions);
  };

  render() {
    return <React.Fragment />;
  }
}

export const mapStateToProps = (state) => ({
  token: state.auth.token,
});
export const mapDispatchToProps = (dispatch) => ({
  getRules: (token, grantId, config, subGrantId) => {
    const action = ApiActions.rules.getRules(token, grantId, config, subGrantId);
    const response = dispatch(action);
    return response;
  },
  getEHPConditions: (token, projectIds) => {
    const action = ApiActions.ehp.getConditions(token, projectIds);
    const response = dispatch(action);
    return response;
  },
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(connectDynamic(TermsAndConditionsListener));
